/**
 * Performance Profiler para auditoría detallada de operaciones create-branch
 *
 * Este módulo instrumenta el flujo completo desde click final hasta repaint,
 * capturando tiempos precisos por etapa para diagnóstico de performance.
 */

export interface ProfileMeasurement {
  stage: string;
  startTime: number;
  endTime: number;
  duration: number;
  metadata?: Record<string, any>;
}

export interface ProfileSession {
  sessionId: string;
  operation: string;
  startTime: number;
  endTime?: number;
  totalDuration?: number;
  measurements: ProfileMeasurement[];
  metadata: Record<string, any>;
}

class PerformanceProfiler {
  private sessions: Map<string, ProfileSession> = new Map();
  private currentSessionId: string | null = null;
  private stageStartTimes: Map<string, number> = new Map();

  /**
   * Inicia una nueva sesión de profiling
   */
  startSession(operation: string, metadata: Record<string, any> = {}): string {
    const sessionId = `${operation}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const startTime = performance.now();

    const session: ProfileSession = {
      sessionId,
      operation,
      startTime,
      measurements: [],
      metadata,
    };

    this.sessions.set(sessionId, session);
    this.currentSessionId = sessionId;

    console.log(`[PERF:${sessionId}] 🚀 Session started: ${operation}`, { startTime, metadata });

    // Performance mark para integración con DevTools
    performance.mark(`${sessionId}:start`);

    return sessionId;
  }

  /**
   * Marca el inicio de una etapa
   */
  markStageStart(sessionId: string, stage: string, metadata?: Record<string, any>): void {
    const startTime = performance.now();
    this.stageStartTimes.set(`${sessionId}:${stage}`, startTime);

    console.log(`[PERF:${sessionId}] ⏱️  Stage START: ${stage}`, { startTime, metadata });
    performance.mark(`${sessionId}:${stage}:start`);
  }

  /**
   * Marca el fin de una etapa y registra medición
   */
  markStageEnd(sessionId: string, stage: string, metadata?: Record<string, any>): void {
    const endTime = performance.now();
    const key = `${sessionId}:${stage}`;
    const startTime = this.stageStartTimes.get(key);

    if (!startTime) {
      console.warn(`[PERF:${sessionId}] ⚠️  No start time for stage: ${stage}`);
      return;
    }

    const duration = endTime - startTime;

    const session = this.sessions.get(sessionId);
    if (!session) {
      console.warn(`[PERF:${sessionId}] ⚠️  Session not found`);
      return;
    }

    const measurement: ProfileMeasurement = {
      stage,
      startTime,
      endTime,
      duration,
      metadata,
    };

    session.measurements.push(measurement);
    this.stageStartTimes.delete(key);

    console.log(`[PERF:${sessionId}] ✅ Stage END: ${stage}`, {
      duration: `${duration.toFixed(2)}ms`,
      metadata
    });

    performance.mark(`${sessionId}:${stage}:end`);
    performance.measure(`${sessionId}:${stage}`, `${sessionId}:${stage}:start`, `${sessionId}:${stage}:end`);
  }

  /**
   * Finaliza la sesión de profiling
   */
  endSession(sessionId: string, metadata?: Record<string, any>): ProfileSession | null {
    const session = this.sessions.get(sessionId);
    if (!session) {
      console.warn(`[PERF:${sessionId}] ⚠️  Session not found`);
      return null;
    }

    const endTime = performance.now();
    const totalDuration = endTime - session.startTime;

    session.endTime = endTime;
    session.totalDuration = totalDuration;
    if (metadata) {
      session.metadata = { ...session.metadata, ...metadata };
    }

    console.log(`[PERF:${sessionId}] 🏁 Session ended: ${session.operation}`, {
      totalDuration: `${totalDuration.toFixed(2)}ms`,
      stageCount: session.measurements.length,
    });

    performance.mark(`${sessionId}:end`);
    performance.measure(sessionId, `${sessionId}:start`, `${sessionId}:end`);

    // Generar reporte
    this.printReport(sessionId);

    if (this.currentSessionId === sessionId) {
      this.currentSessionId = null;
    }

    return session;
  }

  /**
   * Obtiene la sesión actual
   */
  getCurrentSession(): ProfileSession | null {
    if (!this.currentSessionId) return null;
    return this.sessions.get(this.currentSessionId) || null;
  }

  /**
   * Obtiene una sesión específica
   */
  getSession(sessionId: string): ProfileSession | null {
    return this.sessions.get(sessionId) || null;
  }

  /**
   * Genera reporte detallado de una sesión
   */
  printReport(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      console.warn(`[PERF] No session found: ${sessionId}`);
      return;
    }

    console.group(`[PERF REPORT] ${session.operation} (${session.sessionId})`);

    console.log('📊 TOTAL DURATION:', {
      total: `${session.totalDuration?.toFixed(2)}ms`,
      startTime: `${session.startTime.toFixed(2)}ms`,
      endTime: `${session.endTime?.toFixed(2)}ms`,
    });

    console.log('\n📋 METADATA:', session.metadata);

    console.log('\n⏱️  STAGE BREAKDOWN:');
    console.table(
      session.measurements.map((m, i) => ({
        '#': i + 1,
        'Stage': m.stage,
        'Duration (ms)': m.duration.toFixed(2),
        '% of Total': session.totalDuration
          ? ((m.duration / session.totalDuration) * 100).toFixed(1) + '%'
          : 'N/A',
        'Start Offset (ms)': (m.startTime - session.startTime).toFixed(2),
      }))
    );

    // Análisis de cuello de botella
    const sortedByDuration = [...session.measurements].sort((a, b) => b.duration - a.duration);
    console.log('\n🔥 TOP 5 SLOWEST STAGES:');
    console.table(
      sortedByDuration.slice(0, 5).map((m, i) => ({
        'Rank': i + 1,
        'Stage': m.stage,
        'Duration (ms)': m.duration.toFixed(2),
        '% of Total': session.totalDuration
          ? ((m.duration / session.totalDuration) * 100).toFixed(1) + '%'
          : 'N/A',
      }))
    );

    // Análisis de overhead
    const measuredTime = session.measurements.reduce((sum, m) => sum + m.duration, 0);
    const unmeasuredTime = (session.totalDuration || 0) - measuredTime;
    console.log('\n📐 OVERHEAD ANALYSIS:');
    console.table([
      {
        'Category': 'Measured Stages',
        'Time (ms)': measuredTime.toFixed(2),
        '% of Total': session.totalDuration
          ? ((measuredTime / session.totalDuration) * 100).toFixed(1) + '%'
          : 'N/A',
      },
      {
        'Category': 'Unmeasured/Overhead',
        'Time (ms)': unmeasuredTime.toFixed(2),
        '% of Total': session.totalDuration
          ? ((unmeasuredTime / session.totalDuration) * 100).toFixed(1) + '%'
          : 'N/A',
      },
    ]);

    console.groupEnd();
  }

  /**
   * Limpia todas las sesiones
   */
  clear(): void {
    this.sessions.clear();
    this.stageStartTimes.clear();
    this.currentSessionId = null;
    performance.clearMarks();
    performance.clearMeasures();
  }

  /**
   * Obtiene todas las sesiones
   */
  getAllSessions(): ProfileSession[] {
    return Array.from(this.sessions.values());
  }
}

// Singleton export
export const performanceProfiler = new PerformanceProfiler();

// Expose globally for debugging
if (typeof window !== 'undefined') {
  (window as any).__performanceProfiler = performanceProfiler;
}
